import { useState, useRef, useEffect } from "react";

import TypingIndicator from "./TypingIndicator";
import closeButton from "../assets/chatbot/close-button.svg";
import robotWaving from "../assets/chatbot/robot-waving.png";
import { chatbotStyles, chatbotClasses } from "../styles/chatbotStyles";
import MarkdownRenderer from "../utils/MarkdownRenderer";

const ChatbotInterface = ({
  messages,
  onSendMessage,
  onClose,
  isEnlarged,
  onToggleEnlarge,
}) => {
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingStartTimeRef = useRef(null);

  // Calculate if send button should be active during render
  const canSend = inputText.trim().length > 0;

  // Calculate dimensions based on enlarged state
  const dimensions = isEnlarged
    ? { width: "400px", height: "600px" }
    : { width: "280px", height: "420px" };

  // Scroll to bottom when messages change - this is a legitimate side effect
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Only use useEffect for legitimate side effects like DOM manipulation
  useEffect(() => {
    scrollToBottom();

    // Hide typing indicator when a new bot message is received
    if (
      messages.length > 0 &&
      messages[messages.length - 1].isBot &&
      isTyping
    ) {
      const typingStartTime = typingStartTimeRef.current;
      if (typingStartTime) {
        const elapsedTime = Date.now() - typingStartTime;
        const minimumTypingTime = 500; // 0.5 seconds

        if (elapsedTime >= minimumTypingTime) {
          setIsTyping(false);
        } else {
          // Wait for the remaining time before hiding
          setTimeout(() => {
            setIsTyping(false);
          }, minimumTypingTime - elapsedTime);
        }
      } else {
        setIsTyping(false);
      }
    }
  }, [messages, isTyping]);

  // Filter messages to hide the most recent bot message if we're still typing
  const displayMessages =
    isTyping && messages.length > 0 && messages[messages.length - 1].isBot
      ? messages.slice(0, -1)
      : messages;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSend) {
      onSendMessage(inputText.trim());
      setInputText("");
      setIsTyping(true);
      typingStartTimeRef.current = Date.now();
    }
  };

  return (
    <div
      className="relative bg-white overflow-hidden transition-all duration-300 ease-in-out"
      style={{
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: "18px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
      }}
    >
      {/* Header with Omni title and close button */}
      <div className="relative" style={{ height: "40px" }}>
        {/* Omni title - exact Figma positioning */}
        <h3
          className="absolute text-black"
          style={{
            fontFamily: "TWK_Everett, sans-serif",
            fontSize: "18px",
            fontWeight: "500",
            lineHeight: "16px",
            letterSpacing: "-0.72px",
            textAlign: "left",
            left: "13px",
            top: "11px",
            width: "180px",
            height: "16px",
          }}
        >
          Omni
        </h3>

        {/* Enlarge button */}
        <button
          onClick={onToggleEnlarge}
          className="absolute flex items-center justify-center bg-transparent hover:opacity-70 transition-opacity"
          style={{
            width: "15px",
            height: "15px",
            right: isEnlarged ? "50px" : "40px",
            top: "11px",
          }}
          aria-label={isEnlarged ? "Minimize chat" : "Enlarge chat"}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isEnlarged ? (
              // Minimize icon
              <path
                d="M1 11H7V17M17 7H11V1M7 11L1 17M11 7L17 1"
                stroke="#121212"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              // Maximize icon
              <path
                d="M11 1H17V7M7 17H1V11M17 1L11 7M1 17L7 11"
                stroke="#121212"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute flex items-center justify-center bg-transparent hover:opacity-70 transition-opacity"
          style={{
            width: "18",
            height: "18",
            right: "13px",
            top: "9.33px",
          }}
        >
          <img
            src={closeButton}
            alt="Close"
            style={{
              width: "18",
              height: "18",
            }}
          />
        </button>
      </div>

      {/* Messages container with scrolling */}
      <div
        className="absolute overflow-y-auto"
        style={{
          left: "0px",
          top: "40px", // After header
          right: "0px",
          bottom: "65px", // Space for input field
          padding: "0 13px",
        }}
      >
        {/* First Bot Message - positioned exactly per Figma at (60, 67) */}
        {displayMessages.length > 0 && displayMessages[0].isBot && (
          <div
            className="relative"
            style={{
              marginLeft: "47px", // 60px - 13px padding
              marginTop: "27px", // 67px - 40px top
              marginBottom: "24px",
            }}
          >
            {/* Robot Avatar for first message - positioned relative to the message */}
            <div
              className="absolute border border-black overflow-hidden"
              style={{
                width: "40px",
                height: "40px",
                left: "-47px",
                top: "0px",
                borderRadius: "300px",
              }}
            >
              {/* Ellipse 724 - Gray background */}
              <div
                className="absolute bg-[#DEDEDE]"
                style={{
                  left: "0px",
                  top: "0px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                }}
              />
              {/* Wave 1 - Robot avatar image */}
              <img
                src={robotWaving}
                alt="Omni Robot"
                className="absolute"
                style={{
                  left: "2px",
                  top: "9px",
                  transform: "scale(1.4)",
                }}
              />
            </div>

            <div
              className="bg-white text-black chatbot-message"
              style={{
                width: isEnlarged ? "322px" : "202px",
                minHeight: "63px",
                padding: "6px 11px",
                borderRadius: "2px 9px 9px 9px",
                fontFamily: "Creato Display, sans-serif",
                fontSize: "12px",
                fontWeight: "400",
                lineHeight: "16px",
                textAlign: "left",
                boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.25)",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
              }}
            >
              <MarkdownRenderer text={displayMessages[0].text} />
              {displayMessages[0].isStreaming && (
                <span className={chatbotClasses.typingCursor} />
              )}
            </div>
          </div>
        )}

        {/* User Messages and Additional Bot Messages */}
        {displayMessages.slice(1).map((message, index) => {
          const isBot = message.isBot;
          const isFirstUserMessage = !isBot && index === 0;

          return (
            <div
              key={message.id}
              className="relative"
              style={{
                marginBottom: index < displayMessages.length - 2 ? "20px" : "0",
                marginLeft: isBot ? "47px" : "0",
              }}
            >
              {/* Show robot avatar for bot messages after the first */}
              {isBot && index > 0 && (
                <div
                  className="absolute border border-black overflow-hidden"
                  style={{
                    width: "40px",
                    height: "40px",
                    left: "-47px",
                    top: "0px",
                    borderRadius: "300px",
                  }}
                >
                  {/* Ellipse 724 - Gray background */}
                  <div
                    className="absolute bg-[#DEDEDE]"
                    style={{
                      left: "0px",
                      top: "0px",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                    }}
                  />
                  {/* Wave 1 - Robot avatar image */}
                  <img
                    src={robotWaving}
                    alt="Omni Robot"
                    className="absolute"
                    style={{
                      left: "2px",
                      top: "9px",
                      transform: "scale(1.4)",
                    }}
                  />
                </div>
              )}

              <div
                className={`${isBot ? "bg-white" : "bg-[#E5F6FF]"} text-black chatbot-message`}
                style={{
                  width: isEnlarged ? "322px" : "202px",
                  padding: isFirstUserMessage ? "8px 11px" : "6px 11px",
                  borderRadius: isBot ? "2px 9px 9px 9px" : "2px 9px 9px 9px",
                  fontFamily: "Creato Display, sans-serif",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "16px",
                  textAlign: "left",
                  boxShadow: isBot ? "0px 0px 6px rgba(0, 0, 0, 0.25)" : "none",
                  minHeight: isFirstUserMessage ? "34px" : "auto",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  hyphens: "auto",
                }}
              >
                <MarkdownRenderer text={message.text} />
                {message.isStreaming && (
                  <span className={chatbotClasses.typingCursor} />
                )}
              </div>
            </div>
          );
        })}

        {/* Typing Indicator */}
        {isTyping && (
          <div
            className="relative"
            style={{
              marginLeft: "47px",
              marginTop: "20px",
            }}
          >
            {/* Robot avatar for typing */}
            <div
              className="absolute border border-black overflow-hidden"
              style={{
                width: "40px",
                height: "40px",
                left: "-47px",
                top: "0px",
                borderRadius: "300px",
              }}
            >
              {/* Ellipse 724 - Gray background */}
              <div
                className="absolute bg-[#DEDEDE]"
                style={{
                  left: "0px",
                  top: "0px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                }}
              />
              {/* Wave 1 - Robot avatar image */}
              <img
                src={robotWaving}
                alt="Omni Robot"
                className="absolute"
                style={{
                  left: "2px",
                  top: "9px",
                  transform: "scale(1.4)",
                }}
              />
            </div>

            <TypingIndicator />
          </div>
        )}

        <div ref={messagesEndRef} style={{ marginBottom: "16px" }} />
      </div>

      {/* Input container */}
      <div
        className="absolute"
        style={{
          left: "13px",
          bottom: "25px",
          width: isEnlarged ? "374px" : "254px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <div
            className="flex items-center"
            style={{
              gap: "4px",
            }}
          >
            {/* Input field */}
            <div
              className="border border-[#121212] flex items-stretch"
              style={{
                width: isEnlarged ? "323px" : "203px",
                height: "27px",
                borderRadius: "7px",
                padding: "4px 8px",
                gap: "5px",
              }}
            >
              <div
                className="flex items-center flex-1"
                style={{
                  height: "18px",
                  gap: "4px",
                }}
              >
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="What's on your mind?"
                  className="w-full outline-none bg-transparent"
                  style={{
                    fontFamily: "Creato Display, sans-serif",
                    fontSize: "12px",
                    fontWeight: "400",
                    textAlign: "left",
                    color: canSend ? "#000000" : "#5D5D5D", // Black text when typing, gray placeholder
                  }}
                />
              </div>
            </div>

            {/* Send button - exact Figma specifications with active state */}
            <button
              type="submit"
              disabled={!canSend}
              className={`border flex items-center justify-center ${
                canSend ? "bg-black border-black" : "bg-white border-[#999999]"
              }`}
              style={{
                width: "46px",
                height: "28px",
                borderRadius: "6.927272319793701px",
                borderWidth: "1px",
                padding: "0px 11.545453071594238px",
                gap: "5.772726535797119px",
              }}
            >
              {/* Send icon - exact Figma specifications */}
              <div
                style={{
                  width: "23.090909957885742px",
                  height: "23.090909957885742px",
                }}
              >
                <svg
                  width="20"
                  height="14"
                  viewBox="0 0 20 14"
                  fill="none"
                  style={{
                    position: "relative",
                    left: "1.34px",
                    top: "4.5px",
                    width: "20.41px",
                    height: "14px",
                  }}
                >
                  <path
                    d="M13.0046 7.60736e-07L11.6055 1.39904L16.2154 6.00886L0.000526841 6.00886L0.000526581 7.9815L16.2154 7.9815L11.6055 12.5913L13.0046 13.9904L19.9998 6.99518L13.0046 7.60736e-07Z"
                    fill={canSend ? "#FFFFFF" : "#999999"}
                  />
                </svg>
              </div>
            </button>
          </div>
        </form>
      </div>

      {/* CSS styles that can't be converted to Tailwind */}
      <style>{`
        ${chatbotStyles.keyframes}
        ${chatbotStyles.syntaxHighlighting}
        ${chatbotStyles.messageOverrides}
      `}</style>
    </div>
  );
};

export default ChatbotInterface;
